import { useEffect, useMemo, useState } from 'react';
import { Route, Save } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { AutocompleteSelect, Button, Modal, Toggle } from '../../ui';
import { ProviderType, type RoutingPolicy } from '../../gql/types';
import type { UpsertRoutingPolicyInput } from '../../gql/types';
import { TextField } from '../shared/FormField';
import { ProviderOrderEditor } from './ProviderOrderEditor';

// Create/edit form for a routing policy (US-8.3.1). Composes the reorderable provider order,
// an optional capacity threshold, an optional scope (application picker + canonical model), and
// an enabled toggle. Validation requires a name and ≥1 provider before save is allowed.

export interface ApplicationOption {
  id: string;
  name: string;
}

export interface RoutingPolicyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Existing policy to edit, or null to create. */
  policy: RoutingPolicy | null;
  applications: ApplicationOption[];
  loading?: boolean | undefined;
  onSubmit: (input: UpsertRoutingPolicyInput) => void;
}

const NO_APP = '__none__';

function parseThreshold(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

export function RoutingPolicyFormModal({
  isOpen,
  onClose,
  policy,
  applications,
  loading = false,
  onSubmit,
}: RoutingPolicyFormModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isEdit = policy !== null;

  const [name, setName] = useState('');
  const [order, setOrder] = useState<ProviderType[]>([]);
  const [threshold, setThreshold] = useState('');
  const [appId, setAppId] = useState<string>(NO_APP);
  const [canonicalModel, setCanonicalModel] = useState('');
  const [enabled, setEnabled] = useState(true);

  // (Re)seed the form whenever the modal opens or the edited policy changes.
  useEffect(() => {
    if (!isOpen) return;
    setName(policy?.name ?? '');
    setOrder(policy?.providerOrder ?? []);
    setThreshold(
      policy?.capacityThreshold != null ? String(policy.capacityThreshold) : '',
    );
    setAppId(policy?.scopeApplication?.id ?? NO_APP);
    setCanonicalModel(policy?.scopeCanonicalModel ?? '');
    setEnabled(policy?.enabled ?? true);
  }, [isOpen, policy]);

  const trimmedName = name.trim();
  const valid = trimmedName.length > 0 && order.length > 0;

  const appOptions = useMemo(
    () => [
      { value: NO_APP, label: 'All applications (no app scope)' },
      ...applications.map((a) => ({ value: a.id, label: a.name })),
    ],
    [applications],
  );

  const handleSubmit = () => {
    if (!valid) return;
    const input: UpsertRoutingPolicyInput = {
      name: trimmedName,
      providerOrder: order,
      enabled,
      capacityThreshold: parseThreshold(threshold),
      scopeApplicationId: appId === NO_APP ? null : appId,
      scopeCanonicalModel: canonicalModel.trim() === '' ? null : canonicalModel.trim(),
    };
    if (isEdit && policy) input.id = policy.id;
    onSubmit(input);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit routing policy' : 'New routing policy'}
      subtitle="Providers are tried in order; routing fails over when one is unhealthy or at capacity."
      icon={<Route className="h-7 w-7" aria-hidden="true" />}
      footer={
        <>
          <Button variant="secondary" fullWidth onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            fullWidth
            loading={loading}
            disabled={!valid}
            onClick={handleSubmit}
            leftIcon={<Save className="h-4 w-4" aria-hidden="true" />}
          >
            Save policy
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <TextField
          label="Policy name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Internal-first failover"
        />

        <div>
          <span
            className={`mb-2 block text-xs font-medium ${
              isDark ? 'text-white/60' : 'text-slate-600'
            }`}
          >
            Provider order <span aria-hidden="true">*</span>
          </span>
          <ProviderOrderEditor value={order} onChange={setOrder} />
        </div>

        <TextField
          label="Capacity threshold"
          type="number"
          step="0.05"
          min="0"
          max="1"
          inputMode="decimal"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          placeholder="Optional — e.g. 0.8"
          hint="Fraction of capacity (0–1) at which routing spills to the next provider. Leave blank for the default."
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <span
              className={`block text-xs font-medium ${
                isDark ? 'text-white/60' : 'text-slate-600'
              }`}
            >
              Scope: application
            </span>
            <AutocompleteSelect
              label="Scope application"
              value={appId}
              options={appOptions}
              onChange={setAppId}
            />
          </div>
          <TextField
            label="Scope: canonical model"
            value={canonicalModel}
            onChange={(e) => setCanonicalModel(e.target.value)}
            placeholder="Optional — e.g. gpt-4o"
          />
        </div>

        <div
          className={`flex items-center justify-between rounded-xl border p-4 ${
            isDark ? 'border-white/[0.06] bg-white/[0.02]' : 'border-slate-200 bg-slate-50'
          }`}
        >
          <div>
            <span
              className={`text-sm font-medium ${isDark ? 'text-white/80' : 'text-slate-700'}`}
            >
              Policy enabled
            </span>
            <p className={`text-xs ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
              Disabled policies are kept but not applied to routing.
            </p>
          </div>
          <Toggle
            checked={enabled}
            onChange={setEnabled}
            label={enabled ? 'Disable policy' : 'Enable policy'}
          />
        </div>
      </div>
    </Modal>
  );
}
